import { z } from "zod";
import { StableValue } from "../../lib/stable-value";
import { safeParse } from "../../lib/zod";
import { Targets } from "../../introspection";
import {
  ControlDefinition,
  serialize
} from "../definition";
import { DefaultControlInstance } from "../instance";
import { resolveLink } from "./resolve-link";
import * as LinkSchema from "./schema";
class Definition extends ControlDefinition {
  static type = "makeswift::controls::link";
  static get schema() {
    const type = z.literal(this.type);
    const link = LinkSchema.link;
    const data = LinkSchema.data;
    const value = data;
    const config = z.object({
      label: z.string().optional()
    });
    const definition = z.object({
      type,
      config
    });
    const linkTarget = LinkSchema.target;
    const resolvedValue = z.object({
      href: z.string(),
      target: linkTarget.optional(),
      onClick: z.function().args(z.any()).returns(z.void())
    });
    return {
      type,
      data,
      link,
      linkTarget,
      value,
      resolvedValue,
      config,
      definition
    };
  }
  get controlType() {
    return Definition.type;
  }
  get schema() {
    return {
      ...Definition.schema,
      data: Definition.schema.data,
      value: Definition.schema.value,
      resolvedValue: Definition.schema.resolvedValue
    };
  }
  safeParse(data) {
    return safeParse(this.schema.data, data);
  }
  fromData(data) {
    return this.schema.data.optional().parse(data);
  }
  toData(value) {
    return value;
  }
  copyData(data, { replacementContext }) {
    if (data == null)
      return data;
    if (data.type === "OPEN_PAGE") {
      const { pageId } = data.payload;
      if (pageId != null) {
        return {
          ...data,
          payload: {
            ...data.payload,
            pageId: replacementContext.pageIds.get(pageId) ?? pageId
          }
        };
      }
    }
    if (data.type === "SCROLL_TO_ELEMENT") {
      const { elementIdConfig } = data.payload;
      if (elementIdConfig == null)
        return data;
      const { elementKey } = elementIdConfig;
      return {
        ...data,
        payload: {
          ...data.payload,
          elementIdConfig: {
            ...elementIdConfig,
            elementKey: replacementContext.elementKeys.get(elementKey) ?? elementKey
          }
        }
      };
    }
    return data;
  }
  resolveValue(data, resolver) {
    const emptyLink = {
      href: "#",
      onClick: (e) => {
        if (e.defaultPrevented)
          return;
      }
    };
    const pageId = data?.type === "OPEN_PAGE" ? data.payload.pageId : null;
    const pageSub = resolver.resolvePagePathnameSlice(pageId ?? void 0);
    const elementKey = data?.type === "SCROLL_TO_ELEMENT" ? data.payload.elementIdConfig?.elementKey : null;
    const elementIdSub = resolver.resolveElementId(elementKey ?? "");
    const stableValue = StableValue({
      name: Definition.type,
      read: () => {
        if (data == null)
          return emptyLink;
        const page = pageSub.readStable();
        const elementId = elementIdSub.readStable();
        const { href, target, scrollOptions } = resolveLink(
          data,
          page,
          elementId
        );
        return {
          href,
          target,
          onClick: this.resolveOnClick(data, href, scrollOptions)
        };
      },
      deps: [pageSub, elementIdSub]
    });
    return {
      ...stableValue,
      triggerResolve: async () => {
        if (pageSub.readStable() == null) {
          pageSub.fetch();
        }
      }
    };
  }
  resolveOnClick(_data, _href, scrollOptions) {
    const onClick = (event) => {
      if (event.defaultPrevented)
        return;
    };
    onClick.$scrollOptions = scrollOptions;
    return onClick;
  }
  introspect(data, target) {
    if (target.type !== Targets.Page.type)
      return [];
    if (data == null)
      return [];
    switch (data.type) {
      case "OPEN_PAGE":
        return data.payload.pageId != null ? [data.payload.pageId] : [];
      default:
        return [];
    }
  }
  createInstance(sendMessage) {
    return new DefaultControlInstance(sendMessage);
  }
  serialize() {
    return serialize(this.config, {
      type: Definition.type
    });
  }
}
export {
  Definition as LinkDefinition
};
//# sourceMappingURL=link.js.map